import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "@material-ui/core/Button";
import { Payment } from "../../api/payments";
import { ApiError } from "../../api/dispatcher";
import { chargePaymentAndUpdateStore } from "../../actions/payment-actions";
import { setSnackbarMessage } from "../../actions/store/snackbar-actions";
import { SnackbarVariant } from "../../actions/store/type";
import { conf } from "../../conf";
import { format } from "../../util/date-utility";
import {
	getChargeBlockReason,
	getChargeDeadline,
	getReadablePaymentCurrency,
	isTimeSet
} from "../../util/general-utility";

// Text of the charge on the guest's card statement, sent to mollie as entered
// (mollie limit: 255 characters)
const DEFAULT_DESCRIPTION = "Gebühr Tischreservierung Xandl Stadl";
const MAX_DESCRIPTION_LENGTH = 255;

interface State {
	amount: string;
	description: string;
	submitting: boolean;
}

export interface ChargeNoShowDialogProps {
	open: boolean;
	payment: Payment;
	// Date of the visit, if known (otherwise the server resolves it)
	reservationDate?: string | Date;
	checkedIn?: boolean;
	onClose: () => void;
	onCharged?: (payment: Payment) => void;
}

interface ConnectedChargeNoShowDialogProps {
	setSnackbarMessage: (message: string, variant?: SnackbarVariant) => void;
}

type Props = ChargeNoShowDialogProps & ConnectedChargeNoShowDialogProps;

export class ChargeNoShowDialog extends React.Component<Props, State> {
	public state: State = {
		amount: "",
		description: DEFAULT_DESCRIPTION,
		submitting: false
	};

	public componentWillReceiveProps(nextProps: Props) {
		if (!this.props.open && nextProps.open && nextProps.payment) {
			this.setState({
				amount: nextProps.payment.amount.toFixed(2),
				description: DEFAULT_DESCRIPTION,
				submitting: false
			});
		}
	}

	private handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ amount: event.currentTarget.value });
	};

	private handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ description: event.currentTarget.value });
	};

	private getAmount = () => {
		const amount = parseFloat(this.state.amount);

		return isNaN(amount) ? 0 : Math.round(amount * 100) / 100;
	};

	private getAmountError = () => {
		const { payment } = this.props;
		const amount = this.getAmount();

		if (amount <= 0) {
			return "Bitte einen Betrag angeben";
		}

		if (amount > payment.amount) {
			return "Höchstens die vereinbarte Gebühr von " + getReadablePaymentCurrency(payment.currency, payment.amount);
		}

		return null;
	};

	private handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		const { payment, reservationDate, checkedIn, onCharged, onClose } = this.props;

		if (this.state.submitting || this.getAmountError() || this.state.description.trim() === "") return;
		if (getChargeBlockReason(payment, reservationDate, checkedIn, conf.payments.noShowChargeDays)) return;

		this.setState({ submitting: true });

		try {
			const charged = await chargePaymentAndUpdateStore(payment.id, this.getAmount(), this.state.description.trim());

			if (charged && charged.chargeState === "paid") {
				this.props.setSnackbarMessage(
					"No-Show-Gebühr " + getReadablePaymentCurrency(charged.currency, charged.chargeAmount) + " abgebucht!",
					"success"
				);
			} else {
				this.props.setSnackbarMessage("Abbuchung wurde gestartet und wird verarbeitet.", "info");
			}

			if (onCharged) {
				onCharged(charged);
			}

			onClose();
		} catch (e) {
			console.error(e);

			const error = e as ApiError;

			if (error.serverMessage) {
				this.props.setSnackbarMessage(error.serverMessage, "error");
			} else if (!error.status) {
				// No answer (timeout): the charge may still have gone through at mollie.
				// A retry is safe, the server never charges twice.
				this.props.setSnackbarMessage(
					"Keine Antwort vom Server. Bitte die Zahlungen neu laden und den Status prüfen, bevor Sie es erneut versuchen.",
					"warning"
				);
			} else {
				this.props.setSnackbarMessage("Die Abbuchung ist fehlgeschlagen!", "error");
			}

			this.setState({ submitting: false });
		}
	};

	public render() {
		const { open, payment, reservationDate, checkedIn, onClose } = this.props;
		const { amount, description, submitting } = this.state;

		if (!payment) {
			return null;
		}

		const days = conf.payments.noShowChargeDays;
		const blockReason = getChargeBlockReason(payment, reservationDate, checkedIn, days);
		const deadline = getChargeDeadline(isTimeSet(reservationDate) ? reservationDate : payment.reservationDate, days);
		const amountError = this.getAmountError();

		return (
			<Dialog open={open}>
				<form onSubmit={this.handleSubmit}>
					<DialogTitle>No-Show-Gebühr abbuchen</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Die Gebühr wird sofort von der hinterlegten Kreditkarte
							von <strong>{payment.name}</strong> abgebucht und kann hier nicht rückgängig gemacht werden.
							Der Gast wird per E-Mail informiert.
							{deadline && " Abbuchen möglich bis " + format(deadline, "DD.MM.YYYY") + "."}
						</DialogContentText>
						{blockReason ? (
							<DialogContentText color="error">
								{blockReason}
							</DialogContentText>
						) : (
							<React.Fragment>
								<TextField
									autoFocus
									label="Betrag"
									type="number"
									value={amount}
									onChange={this.handleAmountChange}
									disabled={submitting}
									error={Boolean(amountError)}
									helperText={amountError || "Vereinbart: " + getReadablePaymentCurrency(payment.currency, payment.amount)}
									fullWidth
									required
									InputProps={{
										startAdornment: <InputAdornment position="start">€</InputAdornment>
									}}
									inputProps={{
										min: "0.01",
										max: payment.amount.toFixed(2),
										step: "0.01"
									}}
								/>
								<TextField
									label="Beschreibung"
									type="text"
									value={description}
									onChange={this.handleDescriptionChange}
									disabled={submitting}
									helperText="Erscheint auf der Kreditkartenabrechnung des Gastes"
									margin="normal"
									fullWidth
									required
									inputProps={{
										maxLength: MAX_DESCRIPTION_LENGTH
									}}
								/>
							</React.Fragment>
						)}
					</DialogContent>
					<DialogActions>
						<Button
							className="close-charge-no-show-dialog-button"
							disabled={submitting}
							onClick={onClose}
						>
							Abbrechen
						</Button>
						<Button
							className="submit-charge-no-show-button"
							type="submit"
							color="primary"
							disabled={submitting || Boolean(blockReason) || Boolean(amountError) || description.trim() === ""}
						>
							Abbuchen
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		);
	}
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setSnackbarMessage: (message: string, variant?: SnackbarVariant) =>
		dispatch(setSnackbarMessage(message, variant))
});

export const ConnectedChargeNoShowDialog = connect(
	null,
	mapDispatchToProps
)(ChargeNoShowDialog);
